import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Users, Search, Plus, Trash2, Edit2, RefreshCw, ShieldCheck,
  ShieldOff, Save, X, ChevronDown, ChevronUp, Check, UserPlus,
  ToggleLeft, ToggleRight, KeyRound, Mail, Phone, School, Calendar,
  AlertTriangle, Ban, CheckCircle, Filter, Lock, Upload, Download,
  FileSpreadsheet, Eye, FileDown, Table2, GraduationCap, EyeOff,
  Globe,
  Smartphone
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { adminApi, authApi, schoolApi, apiService } from '@/services/api'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'

// ============================================================
// I18N - TRANSLATIONS FR/EN
// ============================================================
type Lang = 'fr' | 'en'

const i18n = {
  fr: {
    pageTitle: 'Gestion des utilisateurs',
    pageSubtitle: 'Administration des comptes et permissions',
    usersTab: 'Utilisateurs',
    importTab: 'Import Excel',
    permissionsTab: 'Permissions',
    searchPlaceholder: 'Rechercher un utilisateur...',
    importExcel: 'Import Excel',
    newUser: 'Nouvel utilisateur',
    noActiveYear: 'Aucune annee academique active',
    noActiveYearDesc: 'Veuillez selectionner une annee academique dans les parametres pour afficher et creer des utilisateurs.',
    filteredView: 'Vue filtree',
    filteredViewDesc: (schoolId: string | number) => `Vous voyez uniquement les utilisateurs de votre etablissement (ID: ${schoolId}). Contactez un administrateur pour voir tous les utilisateurs.`,
    fullView: 'Vue complete',
    loadingYear: 'Chargement annee...',
    activeYear: 'Annee active',
    accessDenied: 'Acces refuse',
    accessDeniedDesc: 'Veuillez contacter votre administrateur systeme pour obtenir les permissions necessaires.',
    understood: 'Compris',
    noUsers: 'Aucun utilisateur trouve',
    filterApplied: (schoolId: string | number) => `Filtre applique : etablissement ${schoolId}`,
    total: 'Total',
    displayed: 'affiches',
    previous: 'Precedent',
    next: 'Suivant',
    page: 'Page',
    step1: 'Etape 1 : Telecharger le modele',
    step1Desc: 'Telechargez le modele Excel pre-rempli avec les champs requis. Remplissez-le avec les donnees de vos utilisateurs puis importez-le.',
    downloadTemplate: 'Telecharger le modele (.xlsx)',
    requiredCols: 'Colonnes requises : first_name, last_name, email, password, role_id, school_id, academic_year_id',
    optionalCols: 'Colonnes optionnelles : phone, status, matricule',
    usersCreatedFor: 'Les utilisateurs seront crees pour l\'annee academique :',
    usersCreatedForSchool: (schoolId: string | number) => `Les utilisateurs seront crees pour votre etablissement (ID: ${schoolId})`,
    step2: 'Etape 2 : Importer le fichier rempli',
    dropFile: 'Glissez-deposez votre fichier Excel ici ou cliquez pour parcourir',
    acceptedFormat: 'Format accepte : .xlsx (Excel)',
    chooseFile: 'Choisir un fichier',
    processing: 'Traitement en cours...',
    step3: 'Etape 3 : Previsualisation',
    previewUsers: (count: number) => `Previsualisation (${count} utilisateurs)`,
    cancel: 'Annuler',
    importUsers: (count: number) => `Importer ${count} utilisateurs`,
    importInProgress: 'Import en cours...',
    firstName: 'Prenom',
    lastName: 'Nom',
    email: 'Email',
    phone: 'Telephone',
    roleId: 'Role ID',
    schoolId: 'Ecole ID',
    yearId: 'Annee ID',
    status: 'Statut',
    matricule: 'Matricule',
    importResults: 'Resultats de l\'import',
    success: 'Succes',
    errors: 'Erreurs',
    selectUser: 'Selectionner un utilisateur',
    search: 'Rechercher...',
    userPermissions: (name: string) => `Permissions de ${name}`,
    noRole: 'Sans role',
    unsavedChanges: 'Modifications non sauvegardees',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    searchPermission: 'Rechercher une permission...',
    selectUserHint: 'Selectionnez un utilisateur',
    selectUserHintDesc: 'Choisissez un utilisateur dans la liste pour gerer ses permissions',
    newUserTitle: 'Nouvel Utilisateur',
    editUserTitle: 'Modifier Utilisateur',
    matriculeLabel: 'Matricule',
    autoGenerated: 'Auto-genere',
    firstNameLabel: 'Prenom',
    lastNameLabel: 'Nom',
    emailLabel: 'Email',
    phoneLabel: 'Telephone',
    passwordLabel: 'Mot de passe',
    passwordEditHint: 'Nouveau mot de passe (laisser vide pour ne pas changer)',
    passwordHint: 'Min 6 caracteres, 1 majuscule, 1 minuscule, 1 chiffre, 1 special',
    roleLabel: 'Role',
    statusLabel: 'Statut',
    schoolLabel: 'Ecole',
    academicYearLabel: 'Annee scolaire',
    select: 'Selectionner...',
    active: 'Actif',
    inactive: 'Inactif',
    suspended: 'Suspendu',
    cancelBtn: 'Annuler',
    createBtn: 'Creer',
    updateBtn: 'Mettre a jour',
    userCreated: 'Utilisateur cree',
    userUpdated: 'Utilisateur mis a jour',
    userDeleted: 'Utilisateur supprime',
    userActivated: 'Utilisateur active',
    userDeactivated: 'Utilisateur desactive',
    twofaEnabled: '2FA active pour cet utilisateur',
    twofaDisabled: '2FA desactive pour cet utilisateur',
    deleteConfirm: 'Supprimer cet utilisateur ?',
    errorCreate: 'Erreur creation',
    errorUpdate: 'Erreur mise a jour',
    errorDelete: 'Erreur suppression',
    error2FA: 'Erreur 2FA',
    errorLoadUsers: 'Erreur chargement utilisateurs',
    errorLoadRoles: 'Erreur chargement roles',
    errorLoadPermissions: 'Erreur chargement permissions',
    errorLoadUserPerms: 'Erreur chargement permissions utilisateur',
    permissionsSaved: 'Permissions sauvegardees avec succes',
    errorSavePermissions: 'Erreur lors de la sauvegarde des permissions',
    templateDownloaded: 'Modele telecharge',
    readyToImport: (count: number) => `${count} utilisateur(s) pret(s) a importer`,
    fileEmpty: 'Le fichier est vide ou mal formate',
    readError: 'Erreur lors de la lecture du fichier Excel',
    fileReadError: 'Erreur lors de la lecture du fichier',
    noDataToImport: 'Aucune donnee a importer',
    importedSuccess: (count: number) => `${count} utilisateur(s) importe(s) avec succes`,
    importErrors: (count: number) => `${count} erreur(s) lors de l'import`,
    restrictedAccess: 'Acces restreint - Permission users_read requise',
    createDenied: 'Vous n\'avez pas la permission de creer des utilisateurs (users_create)',
    readDenied: 'Vous n\'avez pas la permission de voir les utilisateurs (users_read)',
    updateDenied: 'Vous n\'avez pas la permission de modifier des utilisateurs (users_update)',
    deleteDenied: 'Vous n\'avez pas la permission de supprimer des utilisateurs (users_delete)',
    managePermsDenied: "Vous n\'avez pas la permission de gerer les permissions (admin_full)",
    firstNameRequired: 'Prenom requis',
    lastNameRequired: 'Nom requis',
    emailRequired: 'Email requis',
    emailInvalid: 'Email invalide',
    passwordRequired: 'Mot de passe requis',
    passwordMin6: 'Le mot de passe doit contenir au moins 6 caracteres',
    passwordLower: 'Le mot de passe doit contenir au moins une minuscule',
    passwordUpper: 'Le mot de passe doit contenir au moins une majuscule',
    passwordDigit: 'Le mot de passe doit contenir au moins un chiffre',
    passwordSpecial: 'Le mot de passe doit contenir au moins un caractere special (@$!%*?&+#-)',
    roleRequired: 'Role requis',
    schoolRequired: 'Ecole requise',
    yearRequired: 'Annee scolaire requise',
    matriculeRequired: 'Matricule requis (min 9 caracteres)',
    auditUserCreated: (email: string) => `Creation de l'utilisateur ${email}`,
    auditUserUpdated: (email: string) => `Mise a jour de l'utilisateur ${email}`,
    auditUserDeleted: (email: string) => `Suppression de l'utilisateur ${email}`,
    auditUserStatus: (email: string, status: string) => `Changement de statut de l'utilisateur ${email} vers ${status}`,
    auditUser2FA: (email: string, enabled: boolean) => `${enabled ? 'Activation' : 'Desactivation'} du 2FA pour l'utilisateur ${email}`,
    auditPermissionsUpdated: (email: string) => `Mise a jour des permissions de l'utilisateur ${email}`,
    auditImport: (count: number) => `Import de ${count} utilisateurs via Excel`,
    messagesModule: 'Communication',
    messagesRead: 'Voir les communications',
    messagesManage: 'Gerer les communications',
    messagesSendEmail: 'Envoyer des emails',
    messagesSendSMS: 'Envoyer des SMS',
    messagesTemplates: 'Gerer les templates',
    messagesHistory: "Voir l'historique",
    twoFAManagement: 'Gestion 2FA',
    twoFAApp: '2FA Application (TOTP)',
    twoFAEmail: '2FA Email',
    twoFAAppDesc: 'Authentification via application mobile (Google Authenticator, Authy...)',
    twoFAEmailDesc: 'Authentification via code envoye par email',
    enable: 'Activer',
    disable: 'Desactiver',
    error2FAEmail: 'Erreur 2FA email',
    assignedLevels: 'Niveaux assignes',
    assignedClasses: 'Classes assignees',
    selectLevels: 'Selectionner les niveaux',
    selectClasses: 'Selectionner les classes',
    levelsAndClasses: 'Niveaux et classes',
    noLevels: 'Aucun niveau disponible',
    noClasses: 'Aucune classe disponible',
    levelAssigned: 'Niveau assigne',
    classAssigned: 'Classe assignee',
  },
  en: {
    pageTitle: 'User Management',
    pageSubtitle: 'Account and permission administration',
    usersTab: 'Users',
    importTab: 'Excel Import',
    permissionsTab: 'Permissions',
    searchPlaceholder: 'Search for a user...',
    importExcel: 'Import Excel',
    newUser: 'New User',
    noActiveYear: 'No active academic year',
    noActiveYearDesc: 'Please select an academic year in the settings to display and create users.',
    filteredView: 'Filtered view',
    filteredViewDesc: (schoolId: string | number) => `You only see users from your institution (ID: ${schoolId}). Contact an administrator to see all users.`,
    fullView: 'Full view',
    loadingYear: 'Loading year...',
    activeYear: 'Active year',
    accessDenied: 'Access denied',
    accessDeniedDesc: 'Please contact your system administrator to obtain the necessary permissions.',
    understood: 'Understood',
    noUsers: 'No users found',
    filterApplied: (schoolId: string | number) => `Filter applied: institution ${schoolId}`,
    total: 'Total',
    displayed: 'displayed',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    step1: 'Step 1: Download the template',
    step1Desc: 'Download the pre-filled Excel template with required fields. Fill it with your user data then import it.',
    downloadTemplate: 'Download template (.xlsx)',
    requiredCols: 'Required columns: first_name, last_name, email, password, role_id, school_id, academic_year_id',
    optionalCols: 'Optional columns: phone, status, matricule',
    usersCreatedFor: 'Users will be created for the academic year:',
    usersCreatedForSchool: (schoolId: string | number) => `Users will be created for your institution (ID: ${schoolId})`,
    step2: 'Step 2: Import the filled file',
    dropFile: 'Drag and drop your Excel file here or click to browse',
    acceptedFormat: 'Accepted format: .xlsx (Excel)',
    chooseFile: 'Choose a file',
    processing: 'Processing...',
    step3: 'Step 3: Preview',
    previewUsers: (count: number) => `Preview (${count} users)`,
    cancel: 'Cancel',
    importUsers: (count: number) => `Import ${count} users`,
    importInProgress: 'Import in progress...',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    roleId: 'Role ID',
    schoolId: 'School ID',
    yearId: 'Year ID',
    status: 'Status',
    matricule: 'Matricule',
    importResults: 'Import Results',
    success: 'Success',
    errors: 'Errors',
    selectUser: 'Select a user',
    search: 'Search...',
    userPermissions: (name: string) => `${name}'s Permissions`,
    noRole: 'No role',
    unsavedChanges: 'Unsaved changes',
    save: 'Save',
    saving: 'Saving...',
    searchPermission: 'Search for a permission...',
    selectUserHint: 'Select a user',
    selectUserHintDesc: 'Choose a user from the list to manage their permissions',
    newUserTitle: 'New User',
    editUserTitle: 'Edit User',
    matriculeLabel: 'Matricule',
    autoGenerated: 'Auto-generated',
    firstNameLabel: 'First Name',
    lastNameLabel: 'Last Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    passwordLabel: 'Password',
    passwordEditHint: 'New password (leave blank to keep unchanged)',
    passwordHint: 'Min 6 characters, 1 uppercase, 1 lowercase, 1 digit, 1 special',
    roleLabel: 'Role',
    statusLabel: 'Status',
    schoolLabel: 'School',
    academicYearLabel: 'Academic Year',
    select: 'Select...',
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended',
    cancelBtn: 'Cancel',
    createBtn: 'Create',
    updateBtn: 'Update',
    userCreated: 'User created',
    userUpdated: 'User updated',
    userDeleted: 'User deleted',
    userActivated: 'User activated',
    userDeactivated: 'User deactivated',
    twofaEnabled: '2FA enabled for this user',
    twofaDisabled: '2FA disabled for this user',
    deleteConfirm: 'Delete this user?',
    errorCreate: 'Creation error',
    errorUpdate: 'Update error',
    errorDelete: 'Deletion error',
    error2FA: '2FA error',
    errorLoadUsers: 'Error loading users',
    errorLoadRoles: 'Error loading roles',
    errorLoadPermissions: 'Error loading permissions',
    errorLoadUserPerms: 'Error loading user permissions',
    permissionsSaved: 'Permissions saved successfully',
    errorSavePermissions: 'Error saving permissions',
    templateDownloaded: 'Template downloaded',
    readyToImport: (count: number) => `${count} user(s) ready to import`,
    fileEmpty: 'The file is empty or malformed',
    readError: 'Error reading the Excel file',
    fileReadError: 'Error reading the file',
    noDataToImport: 'No data to import',
    importedSuccess: (count: number) => `${count} user(s) imported successfully`,
    importErrors: (count: number) => `${count} error(s) during import`,
    restrictedAccess: 'Restricted access - users_read permission required',
    createDenied: 'You do not have permission to create users (users_create)',
    readDenied: 'You do not have permission to view users (users_read)',
    updateDenied: 'You do not have permission to update users (users_update)',
    deleteDenied: 'You do not have permission to delete users (users_delete)',
    managePermsDenied: 'You do not have permission to manage permissions (admin_full)',
    firstNameRequired: 'First name required',
    lastNameRequired: 'Last name required',
    emailRequired: 'Email required',
    emailInvalid: 'Invalid email',
    passwordRequired: 'Password required',
    passwordMin6: 'Password must contain at least 6 characters',
    passwordLower: 'Password must contain at least one lowercase letter',
    passwordUpper: 'Password must contain at least one uppercase letter',
    passwordDigit: 'Password must contain at least one digit',
    passwordSpecial: 'Password must contain at least one special character (@$!%*?&+#-)',
    roleRequired: 'Role required',
    schoolRequired: 'School required',
    yearRequired: 'Academic year required',
    matriculeRequired: 'Matricule required (min 9 characters)',
    auditUserCreated: (email: string) => `Created user ${email}`,
    auditUserUpdated: (email: string) => `Updated user ${email}`,
    auditUserDeleted: (email: string) => `Deleted user ${email}`,
    auditUserStatus: (email: string, status: string) => `Changed user ${email} status to ${status}`,
    auditUser2FA: (email: string, enabled: boolean) => `${enabled ? 'Enabled' : 'Disabled'} 2FA for user ${email}`,
    auditPermissionsUpdated: (email: string) => `Updated permissions for user ${email}`,
    auditImport: (count: number) => `Imported ${count} users via Excel`,
    messagesModule: 'Communication',
    messagesRead: 'View communications',
    messagesManage: 'Manage communications',
    messagesSendEmail: 'Send emails',
    messagesSendSMS: 'Send SMS',
    messagesTemplates: 'Manage templates',
    messagesHistory: 'View history',
    twoFAManagement: '2FA Management',
    twoFAApp: 'App 2FA (TOTP)',
    twoFAEmail: 'Email 2FA',
    twoFAAppDesc: 'Authentication via mobile app (Google Authenticator, Authy...)',
    twoFAEmailDesc: 'Authentication via email code',
    enable: 'Enable',
    disable: 'Disable',
    error2FAEmail: 'Email 2FA error',
    assignedLevels: 'Assigned levels',
    assignedClasses: 'Assigned classes',
    selectLevels: 'Select levels',
    selectClasses: 'Select classes',
    levelsAndClasses: 'Levels and classes',
    noLevels: 'No levels available',
    noClasses: 'No classes available',
    levelAssigned: 'Level assigned',
    classAssigned: 'Class assigned',
  }
}

function useTranslation() {
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem('app_language') as Lang
    return stored === 'en' ? 'en' : 'fr'
  })

  const t = useMemo(() => i18n[lang], [lang])

  const toggleLang = useCallback(() => {
    const newLang: Lang = lang === 'fr' ? 'en' : 'fr'
    setLang(newLang)
    localStorage.setItem('app_language', newLang)
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: newLang }))
  }, [lang])

  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<Lang>
      if (customEvent.detail && (customEvent.detail === 'fr' || customEvent.detail === 'en')) {
        setLang(customEvent.detail)
      }
    }
    window.addEventListener('languageChanged', handler)
    return () => window.removeEventListener('languageChanged', handler)
  }, [])

  return { t, lang, toggleLang }
}

interface Role { id: number; name: string; description?: string; is_system: boolean }
interface Permission { id: number; name: string; code: string; description?: string; module: string; submodule?: string }
interface AppUser { id: number; first_name: string; last_name: string; email: string; phone?: string; matricule?: string; role_id?: number; role_name?: string; school_id?: number; school_name?: string; academic_year_id?: number; academic_year_label?: string; status: string; is_2fa_enabled?: boolean; email_2fa_enabled?: boolean; created_at?: string; assigned_level_ids?: number[]; assigned_class_ids?: number[] }
interface School { id: number; name: string; short_name: string }
interface AcademicYear { id: number; label: string; is_selected?: boolean; start_date?: string; end_date?: string }
interface ExcelUserRow { first_name: string; last_name: string; email: string; phone?: string; password: string; role_id: number; school_id: number; academic_year_id: number; status: string; matricule?: string }

const USER_STATUSES = [
  { value: 'active', label: { fr: 'Actif', en: 'Active' }, color: 'bg-green-100 text-green-700' },
  { value: 'inactive', label: { fr: 'Inactif', en: 'Inactive' }, color: 'bg-gray-100 text-gray-700' },
  { value: 'suspended', label: { fr: 'Suspendu', en: 'Suspended' }, color: 'bg-red-100 text-red-700' },
]

function generateMatricule(roleName?: string): string {
  const prefix = 'lbb'
  const roleCode = roleName ? roleName.substring(0, 2).toUpperCase() : 'XX'
  const timestamp = Date.now().toString(36).toUpperCase().slice(-4)
  const random = Math.random().toString(36).substring(2, 5).toUpperCase()
  return `${prefix}${roleCode}${timestamp}${random}`
}

function validatePassword(password: string, t: typeof i18n.fr): { valid: boolean; message: string } {
  if (password.length < 6) return { valid: false, message: t.passwordMin6 }
  if (!/[a-z]/.test(password)) return { valid: false, message: t.passwordLower }
  if (!/[A-Z]/.test(password)) return { valid: false, message: t.passwordUpper }
  if (!/\d/.test(password)) return { valid: false, message: t.passwordDigit }
  if (!/[@$!%*?&+#-]/.test(password)) return { valid: false, message: t.passwordSpecial }
  return { valid: true, message: '' }
}

function handleApiError(error: any, fallbackMessage: string = 'Une erreur est survenue'): string {
  if (error?.response?.status === 403) {
    const detail = error?.response?.data?.detail || "Acces refuse. Vous n'avez pas les permissions necessaires."
    return detail
  }
  return error?.response?.data?.detail || error?.message || fallbackMessage
}

// ============================================================
// AUDIT LOG HELPER
// ============================================================
async function logAudit(action: string, description: string, entityType: string = 'user', entityId?: number | string) {
  // V-FIX : via apiService (intercepteur auth JWT correct + baseURL local).
  // Repli sur /audit/log si /audit/logs n'accepte pas le POST (405).
  const body = {
    action, description,
    entity_type: entityType,
    entity_id: entityId?.toString() || null,
    module: 'users',
    timestamp: new Date().toISOString()
  }
  for (const path of ['/audit/logs', '/audit/log']) {
    try {
      await apiService.post(path, body)
      return
    } catch { /* chemin suivant */ }
  }
  console.warn('[Audit] Aucun endpoint d\'audit disponible (logs/log)')
}

const PERMISSION_MODULES = [
  { id: 'users', label: { fr: 'Utilisateurs', en: 'Users' }, permissions: [
    { code: 'users_create', label: { fr: 'Creer des utilisateurs', en: 'Create users' } },
    { code: 'users_read', label: { fr: 'Voir les utilisateurs', en: 'View users' } },
    { code: 'users_update', label: { fr: 'Modifier les utilisateurs', en: 'Update users' } },
    { code: 'users_delete', label: { fr: 'Supprimer les utilisateurs', en: 'Delete users' } },
  ]},
  { id: 'students', label: { fr: 'Eleves', en: 'Students' }, permissions: [
    { code: 'students_create', label: { fr: 'Creer des eleves', en: 'Create students' } },
    { code: 'students_read', label: { fr: 'Voir les eleves', en: 'View students' } },
    { code: 'students_update', label: { fr: 'Modifier les eleves', en: 'Update students' } },
    { code: 'students_delete', label: { fr: 'Supprimer des eleves', en: 'Delete students' } },
  ]},
  { id: 'academics', label: { fr: 'Academique', en: 'Academic' }, permissions: [
    { code: 'specialties_read', label: { fr: 'Voir les specialites & series', en: 'View specialties & series' } },
    { code: 'levels_read', label: { fr: 'Voir les niveaux & classes', en: 'View levels & classes' } },
    { code: 'assign_student', label: { fr: "Mettre l'eleve en classe", en: 'Assign student to class' } },
    { code: 'class_council_read', label: { fr: 'Conseil de classe', en: 'Class council' } },
    { code: 'class_council_express', label: { fr: 'Conseil de classe express', en: 'Express class council' } },
    { code: 'discipline_read', label: { fr: 'Voir la discipline', en: 'View discipline' } },
    { code: 'discipline_write', label: { fr: 'Gerer la discipline', en: 'Manage discipline' } },
  ]},
  { id: 'grades', label: { fr: 'Notes & Bulletins', en: 'Grades & Reports' }, permissions: [
    { code: 'grades_create', label: { fr: 'Creer des notes', en: 'Create grades' } },
    { code: 'grades_read', label: { fr: 'Voir les notes', en: 'View grades' } },
    { code: 'grades_update', label: { fr: 'Modifier les notes', en: 'Update grades' } },
    { code: 'grades_approve', label: { fr: 'Approuver les notes', en: 'Approve grades' } },
    { code: 'bulletins_read', label: { fr: 'Voir les bulletins', en: 'View reports' } },
    { code: 'bulletins_export', label: { fr: 'Exporter les bulletins', en: 'Export reports' } },
  ]},
  { id: 'timetable', label: { fr: 'Emploi du temps', en: 'Timetable' }, permissions: [
    { code: 'timetable_read', label: { fr: "Voir l'emploi du temps", en: 'View timetable' } },
    { code: 'timetable_create', label: { fr: "Creer l'emploi du temps", en: 'Create timetable' } },
  ]},
  { id: 'statistics', label: { fr: 'Statistiques & Rapports', en: 'Statistics & Reports' }, permissions: [
    { code: 'statistics_read', label: { fr: 'Voir les statistiques', en: 'View statistics' } },
    { code: 'statistics_niveau_read', label: { fr: 'Statistiques par niveau (séquence/trimestre/annuelle)', en: 'Level statistics (sequence/term/annual)' } },
    { code: 'list_provisoire_read', label: { fr: 'Liste provisoire', en: 'Provisional list' } },
    { code: 'list_definitive_read', label: { fr: 'Liste definitive', en: 'Definitive list' } },
    { code: 'effectifs_read', label: { fr: 'Effectif des classes', en: 'Class enrollments' } },
    { code: 'saisie_notes_read', label: { fr: 'Fiche de saisie des notes', en: 'Grade entry form' } },
    { code: 'pv_read', label: { fr: 'Proces-verbaux', en: 'Transcripts' } },
    { code: 'livret_read', label: { fr: 'Livret BEPC DECC', en: 'BEPC DECC booklet' } },
    { code: 'majors_read', label: { fr: 'Liste des majors', en: 'Top students' } },
    { code: 'taux_reussite_read', label: { fr: 'Taux de reussite', en: 'Pass rates' } },
    { code: 'decisions_conseil_read', label: { fr: 'Decisions du conseil (admis/exclus/redoublants)', en: 'Council decisions' } },
    { code: 'reports_export', label: { fr: 'Exporter les rapports', en: 'Export reports' } },
    { code: 'reports_print', label: { fr: 'Imprimer les rapports', en: 'Print reports' } },
  ]},
  { id: 'content', label: { fr: 'Contenu Public', en: 'Public Content' }, permissions: [
    { code: 'news_create', label: { fr: 'Creer des actualites', en: 'Create news' } },
    { code: 'news_publish', label: { fr: 'Publier des actualites', en: 'Publish news' } },
    { code: 'events_create', label: { fr: 'Creer des evenements', en: 'Create events' } },
    { code: 'forum_create', label: { fr: 'Creer dans le forum', en: 'Create in forum' } },
  ]},
  { id: 'budget', label: { fr: 'Budget', en: 'Budget' }, permissions: [
    { code: 'budget_read', label: { fr: 'Voir le budget', en: 'View budget' } },
    { code: 'budget_write', label: { fr: 'Modifier le budget', en: 'Edit budget' } },
    { code: 'budget_approve', label: { fr: 'Approuver le budget', en: 'Approve budget' } },
  ]},
  { id: 'fees', label: { fr: 'Scolarite', en: 'School Fees' }, permissions: [
    { code: 'fees_read', label: { fr: 'Voir les frais scolaires', en: 'View school fees' } },
    { code: 'fees_write', label: { fr: 'Enregistrer des paiements', en: 'Record payments' } },
    { code: 'fees_manage', label: { fr: 'Gerer les structures de frais', en: 'Manage fee structures' } },
  ]},
  { id: 'audit', label: { fr: 'Audit & Logs', en: 'Audit & Logs' }, permissions: [
    { code: 'audit_read', label: { fr: "Voir les logs d'audit", en: 'View audit logs' } },
  ]},
  { id: 'admin', label: { fr: 'Administration', en: 'Administration' }, permissions: [
    { code: 'admin_full', label: { fr: 'Acces complet admin', en: 'Full admin access' } },
    { code: 'settings_read', label: { fr: 'Voir les parametres', en: 'View settings' } },
    { code: 'settings_update', label: { fr: 'Modifier les parametres', en: 'Update settings' } },
  ]},
  { id: 'messages', label: { fr: 'Communication', en: 'Communication' }, permissions: [
    { code: 'messages_read', label: { fr: 'Voir les communications', en: 'View communications' } },
    { code: 'messages_manage', label: { fr: 'Gerer les communications', en: 'Manage communications' } },
    { code: 'messages_send_email', label: { fr: 'Envoyer des emails', en: 'Send emails' } },
    { code: 'messages_send_sms', label: { fr: 'Envoyer des SMS', en: 'Send SMS' } },
    { code: 'messages_templates', label: { fr: 'Gerer les templates', en: 'Manage templates' } },
    { code: 'messages_history', label: { fr: "Voir l'historique", en: 'View history' } },
  ]},
]

const ALL_PERMISSION_CODES = PERMISSION_MODULES.flatMap(m => m.permissions.map(p => p.code))
// fees_read, fees_write, fees_manage sont inclus automatiquement via PERMISSION_MODULES

type ViewMode = 'users' | 'permissions' | 'import'
const FULL_ACCESS_ROLES = ['super_admin', 'admin', 'proviseur']

export default function UsersPage() {
  const { t, lang, toggleLang } = useTranslation()
  const { user, permissions: userPermissionsList, role } = useAuthStore()

  const hasFullAccess = FULL_ACCESS_ROLES.includes(role || '')
  const hasPermission = (perm: string): boolean => {
    if (hasFullAccess) return true
    return userPermissionsList?.includes(perm) || userPermissionsList?.includes('admin_full') || false
  }
  const canCreateUser = hasPermission('users_create')
  const canReadUsers = hasPermission('users_read')
  const canUpdateUser = hasPermission('users_update')
  const canDeleteUser = hasPermission('users_delete')
  const canManagePermissions = hasFullAccess || hasPermission('admin_full')

  const canViewAllUsers = hasFullAccess || hasPermission('admin_full')

  const [viewMode, setViewMode] = useState<ViewMode>('users')
  const [users, setUsers] = useState<AppUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [userPage, setUserPage] = useState(1)
  const [userTotal, setUserTotal] = useState(0)
  const [showUserModal, setShowUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<AppUser | null>(null)
  const [userFormData, setUserFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '',
    role_id: '', school_id: '', academic_year_id: '', matricule: '', status: 'active',
  })
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>({})
  const [showNewPassword, setShowNewPassword] = useState(false)

  // ===== NIVEAUX ET CLASSES ASSIGNES =====
  const [levels, setLevels] = useState<Array<{id: number; name: string}>>([])
  const [classes, setClasses] = useState<Array<{id: number; name: string; level_id?: number}>>([])
  const [assignedLevelIds, setAssignedLevelIds] = useState<number[]>([])
  const [assignedClassIds, setAssignedClassIds] = useState<number[]>([])
  const [selectedRoleName, setSelectedRoleName] = useState<string>('')
  const [assignmentsLoading, setAssignmentsLoading] = useState(false)

  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<AppUser | null>(null)
  const [userPermissions, setUserPermissions] = useState<string[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [savingPermissions, setSavingPermissions] = useState(false)
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({
    users: true, students: true, academics: true, grades: true, timetable: true,
    statistics: true, content: true, budget: true, fees: true, audit: true, admin: true, messages: true
  })
  const [permissionSearch, setPermissionSearch] = useState('')
  const [hasUnsavedPermissions, setHasUnsavedPermissions] = useState(false)
  const [pendingPermissions, setPendingPermissions] = useState<Record<string, boolean>>({})

  const [schools, setSchools] = useState<School[]>([])
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [activeAcademicYear, setActiveAcademicYear] = useState<AcademicYear | null>(null)
  const [schoolsLoading, setSchoolsLoading] = useState(false)
  const [activeYearLoading, setActiveYearLoading] = useState(false)

  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false)
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('')

  // ===== 2FA MODAL STATES =====
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [selectedUserFor2FA, setSelectedUserFor2FA] = useState<AppUser | null>(null)
  const [toggling2FA, setToggling2FA] = useState(false)

  const [excelPreviewData, setExcelPreviewData] = useState<ExcelUserRow[]>([])
  const [excelImportLoading, setExcelImportLoading] = useState(false)
  const [excelImportResults, setExcelImportResults] = useState<{ success: number; errors: number; details: string[] } | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState<number | null>(null)
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('')
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ===== INFOS UTILISATEUR CONNECTE - FORCE NUMBER =====
  const currentUserSchoolId = useMemo(() => {
    const raw = (user as any)?.school_id
    const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw
    console.log('[UsersPage] currentUserSchoolId raw:', raw, 'parsed:', parsed, 'type:', typeof parsed)
    return (typeof parsed === 'number' && !isNaN(parsed)) ? parsed : undefined
  }, [user])

  // ===== ANNEE ACADEMIQUE ACTIVE =====
  const loadActiveAcademicYear = useCallback(async () => {
    if (!currentUserSchoolId) {
      console.warn('[UsersPage] Utilisateur connecte sans school_id')
      return
    }
    setActiveYearLoading(true)
    try {
      const res = await schoolApi.getAcademicYears(currentUserSchoolId)
      const years = Array.isArray(res) ? res : res?.data || []
      setAcademicYears(years)
      const active = years.find((y: AcademicYear) => y.is_selected === true)
      if (active) {
        setActiveAcademicYear(active)
        console.log('[UsersPage] Annee academique active:', active.label, '(ID:', active.id, ')')
      } else if (years.length > 0) {
        setActiveAcademicYear(years[0])
        console.warn('[UsersPage] Aucune annee avec is_selected=true, fallback sur:', years[0].label)
      }
    } catch (err) {
      console.error('[UsersPage] Error loading active academic year:', err)
    } finally {
      setActiveYearLoading(false)
    }
  }, [currentUserSchoolId])

  // ===== LOAD USERS - UTILISE authApi.getUsers AVEC school_id =====
  const loadUsers = useCallback(async () => {
    if (!canReadUsers) {
      setAccessDeniedMessage(t.readDenied)
      setShowAccessDeniedModal(true)
      return
    }
    setUsersLoading(true)
    try {
      console.log('[UsersPage] loadUsers START - canViewAllUsers:', canViewAllUsers, 'currentUserSchoolId:', currentUserSchoolId)

      const params: any = { page: userPage, per_page: 20, search: userSearch || undefined }
      if (selectedRoleFilter) params.role_id = parseInt(selectedRoleFilter)
      if (selectedStatusFilter) params.status = selectedStatusFilter

      // Si l'utilisateur n'a PAS les droits de voir tous les utilisateurs,
      // on AJOUTE school_id aux params (maintenant supporte par authApi.getUsers)
      const schoolIdToFilter = selectedSchoolFilter !== null ? selectedSchoolFilter : currentUserSchoolId
      if (!canViewAllUsers && schoolIdToFilter !== undefined) {
        params.school_id = schoolIdToFilter
        console.log('[UsersPage] Ajout school_id au filtre API:', schoolIdToFilter)
      }

      console.log('[UsersPage] Appel authApi.getUsers avec params:', params)
      const res = await authApi.getUsers(params)
      console.log('[UsersPage] authApi.getUsers response:', res)

      const rawUsers = res?.data || []
      console.log('[UsersPage] Raw users count:', rawUsers.length)
      console.log('[UsersPage] Sample user school_ids:', rawUsers.slice(0, 3).map((u: any) => ({ id: u.id, email: u.email, school_id: u.school_id })))

      // FILTRAGE CLIENT-SIDE DE SECURITE (double verification)
      let finalUsers = rawUsers
      const filterSchoolId = selectedSchoolFilter !== null ? selectedSchoolFilter : currentUserSchoolId
      if (!canViewAllUsers && filterSchoolId !== undefined) {
        console.log('[UsersPage] Filtrage client-side par school_id:', filterSchoolId)
        finalUsers = rawUsers.filter((u: AppUser) => {
          const userSchoolId = typeof u.school_id === 'string' ? parseInt(u.school_id, 10) : u.school_id
          const match = userSchoolId === filterSchoolId
          if (!match) {
            console.log(`[UsersPage] EXCLU: user ${u.email} (school_id=${userSchoolId}) !== ${currentUserSchoolId}`)
          }
          return match
        })
        console.log('[UsersPage] Apres filtrage client:', finalUsers.length, 'sur', rawUsers.length)
      }

      setUsers(finalUsers)
      setUserTotal(res?.total || 0)
    } catch (err: any) {
      console.error('[UsersPage] loadUsers error:', err)
      const errorMsg = handleApiError(err, t.errorLoadUsers)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    } finally { setUsersLoading(false) }
  }, [userPage, userSearch, canReadUsers, canViewAllUsers, currentUserSchoolId, selectedSchoolFilter, selectedRoleFilter, selectedStatusFilter, t])

  const loadRoles = useCallback(async () => {
    try {
      const res = await adminApi.getRoles()
      setRoles(Array.isArray(res) ? res : res?.data || [])
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.errorLoadRoles)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      console.error('Error loading roles:', err)
      setRoles([])
    }
  }, [t])

  const loadPermissions = useCallback(async () => {
    setPermissionsLoading(true)
    try {
      let res
      try { res = await adminApi.getPermissions() }
      catch (adminErr: any) {
        console.warn('adminApi.getPermissions failed, trying schoolApi:', adminErr?.message || adminErr)
        try { res = await (schoolApi as any).getPermissions() }
        catch (schoolErr: any) { console.warn('schoolApi.getPermissions also failed:', schoolErr?.message || schoolErr); throw schoolErr }
      }
      const perms = Array.isArray(res) ? res : res?.data || []
      setPermissions(perms)
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.errorLoadPermissions)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      console.error('Error loading permissions - using static fallback:', err?.message || err)
      const staticPermissions: Permission[] = PERMISSION_MODULES.flatMap(m => m.permissions.map(p => ({ id: 0, name: p.label[lang], code: p.code, description: p.label[lang], module: m.id, submodule: undefined })))
      setPermissions(staticPermissions)
      toast.info('Permissions chargees depuis la configuration locale')
    } finally { setPermissionsLoading(false) }
  }, [lang, t])

  const loadUserPermissions = useCallback(async (userId: number) => {
    if (!canManagePermissions) {
      setAccessDeniedMessage(t.managePermsDenied)
      setShowAccessDeniedModal(true)
      return
    }
    setPermissionsLoading(true)
    try {
      const res = await authApi.getUserPermissions(userId)
      const perms = res?.permissions || []
      setUserPermissions(perms)
      const pending: Record<string, boolean> = {}
      ALL_PERMISSION_CODES.forEach(code => { pending[code] = false })
      perms.forEach((code: string) => { pending[code] = true })
      setPendingPermissions(pending)
      setHasUnsavedPermissions(false)
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.errorLoadUserPerms)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      console.error('Error loading user permissions:', err)
      const pending: Record<string, boolean> = {}
      ALL_PERMISSION_CODES.forEach(code => { pending[code] = false })
      setUserPermissions([])
      setPendingPermissions(pending)
    } finally { setPermissionsLoading(false) }
  }, [canManagePermissions, t])

  const loadSchools = useCallback(async () => {
    setSchoolsLoading(true)
    try {
      const res = await schoolApi.getSchools()
      const schoolsData = Array.isArray(res) ? res : res?.data || []
      console.log('[UsersPage] Schools loaded:', schoolsData.length, schoolsData.map((s: any) => ({ id: s.id, name: s.name })))
      setSchools(schoolsData)
    } catch (err) { console.error('[UsersPage] Error loading schools:', err); setSchools([]) }
    finally { setSchoolsLoading(false) }
  }, [])

  const loadLevels = useCallback(async (schoolId: number) => {
    try {
      const res: any = await apiService.get('/levels', { params: { school_id: schoolId, per_page: 1000 } })
      const data = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      setLevels(data)
    } catch (e) { console.error('[UsersPage] Error loading levels:', e); setLevels([]) }
  }, [])

  const loadClasses = useCallback(async (schoolId: number) => {
    try {
      const res: any = await apiService.get('/classes', { params: { school_id: schoolId, per_page: 1000 } })
      const data = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      setClasses(data)
    } catch (e) { console.error('[UsersPage] Error loading classes:', e); setClasses([]) }
  }, [])

  const loadUserAssignments = useCallback(async (userId: number) => {
    setAssignmentsLoading(true)
    try {
      const res: any = await apiService.get(`/auth/users/${userId}/assignments`)
      const data = res.data || res
      setAssignedLevelIds(data.assigned_level_ids || [])
      setAssignedClassIds(data.assigned_class_ids || [])
    } catch (e) { console.error('[UsersPage] Error loading assignments:', e); setAssignedLevelIds([]); setAssignedClassIds([]) }
    finally { setAssignmentsLoading(false) }
  }, [])

  const saveUserAssignments = useCallback(async (userId: number, levelIds: number[], classIds: number[]) => {
    try {
      const res: any = await apiService.put(`/auth/users/${userId}/assignments`, {
        assigned_level_ids: levelIds,
        assigned_class_ids: classIds,
      })
      console.log('[UsersPage] Assignments saved for user', userId, ':', res)
      return true
    } catch (e: any) {
      console.error('[UsersPage] Error saving assignments:', e)
      toast.error(lang === 'fr' ? 'Erreur sauvegarde assignations: ' + (e.response?.data?.detail || e.message) : 'Error saving assignments: ' + (e.response?.data?.detail || e.message))
      return false
    }
  }, [lang])

  // ✅ Permissions de saisie de notes accordées AUTOMATIQUEMENT aux enseignants.
  // Un enseignant sans grades_update ne peut PAS enregistrer ses notes —
  // avant ce fix, la création de compte n'accordait aucune permission.
  const TEACHER_DEFAULT_PERMISSIONS = ['grades_create', 'grades_read', 'grades_update']
  const isTeacherRoleName = (name: string) => ['teacher', 'enseignant'].includes((name || '').toLowerCase())

  const grantDefaultTeacherPermissions = async (userId: number, roleName: string, merge = false) => {
    if (!isTeacherRoleName(roleName)) return
    try {
      let perms = TEACHER_DEFAULT_PERMISSIONS
      if (merge) {
        // FUSION avec les permissions existantes — le backend ÉCRASE tout le
        // set lors du PUT : envoyer seulement les défauts effacerait le reste.
        const res: any = await authApi.getUserPermissions(userId).catch(() => null)
        const current: string[] = res?.permissions || []
        perms = Array.from(new Set([...current, ...TEACHER_DEFAULT_PERMISSIONS]))
      }
      await apiService.put(`/auth/users/${userId}/permissions`, { permissions: perms })
      if (!merge) {
        toast.success(lang === 'fr' ? 'Permissions enseignant attribuées (saisie de notes)' : 'Teacher permissions granted (grade entry)')
      }
    } catch (e) {
      console.warn('[UsersPage] Attribution automatique permissions enseignant échouée:', e)
      toast.warning(lang === 'fr'
        ? "Compte créé, mais les permissions de saisie de notes n'ont pas pu être attribuées automatiquement — attribuez-les dans l'onglet Permissions."
        : 'Account created, but grade permissions could not be granted automatically — grant them in the Permissions tab.')
    }
  }


  const loadAcademicYears = useCallback(async (sid?: number) => {
    try {
      const schoolIdToUse = sid || schools[0]?.id || currentUserSchoolId
      if (!schoolIdToUse) return
      const res = await schoolApi.getAcademicYears(schoolIdToUse)
      setAcademicYears(Array.isArray(res) ? res : res?.data || [])
    } catch (err) { console.error('Error loading academic years:', err); setAcademicYears([]) }
  }, [schools, currentUserSchoolId])

  useEffect(() => { loadActiveAcademicYear() }, [loadActiveAcademicYear])
  useEffect(() => { loadUsers() }, [loadUsers])
  useEffect(() => { loadRoles(); loadPermissions() }, [loadRoles, loadPermissions])
  useEffect(() => { loadSchools() }, [loadSchools])
  useEffect(() => {
    if (selectedUserForPermissions) { loadUserPermissions(selectedUserForPermissions.id) }
  }, [selectedUserForPermissions, loadUserPermissions])

  const validateUserForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!userFormData.first_name.trim()) errors.first_name = t.firstNameRequired
    if (!userFormData.last_name.trim()) errors.last_name = t.lastNameRequired
    if (!userFormData.email.trim()) errors.email = t.emailRequired
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userFormData.email)) errors.email = t.emailInvalid
    if (!editingUser) {
      const pwdValidation = validatePassword(userFormData.password, t)
      if (!pwdValidation.valid) errors.password = pwdValidation.message
    } else if (userFormData.password && userFormData.password.trim().length > 0) {
      const pwdValidation = validatePassword(userFormData.password, t)
      if (!pwdValidation.valid) errors.password = pwdValidation.message
    }
    if (!userFormData.role_id) errors.role_id = t.roleRequired
    if (!userFormData.school_id) errors.school_id = t.schoolRequired
    if (!userFormData.academic_year_id) errors.academic_year_id = t.yearRequired
    if (!userFormData.matricule || userFormData.matricule.length < 9) errors.matricule = t.matriculeRequired
    setUserFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleRoleChange = (roleId: string) => {
    const selectedRole = roles.find(r => r.id.toString() === roleId)
    const roleName = selectedRole?.name || ''
    const newMatricule = generateMatricule(roleName)
    setSelectedRoleName(roleName)
    setUserFormData(prev => ({ ...prev, role_id: roleId, matricule: newMatricule }))

    // Reinitialiser les assignations si ce n'est pas un role concerne
    if (!['surveillant_general', 'censeur', 'surveillant'].includes(roleName.toLowerCase())) {
      setAssignedLevelIds([])
      setAssignedClassIds([])
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canCreateUser) { setAccessDeniedMessage(t.createDenied); setShowAccessDeniedModal(true); return }
    if (!validateUserForm()) return
    try {
      const payload = {
        first_name: userFormData.first_name, last_name: userFormData.last_name,
        email: userFormData.email, phone: userFormData.phone, password: userFormData.password,
        role_id: parseInt(userFormData.role_id), school_id: parseInt(userFormData.school_id),
        academic_year_id: parseInt(userFormData.academic_year_id),
        matricule: userFormData.matricule, status: userFormData.status,
      }
      const created: any = await authApi.createUser(payload)
      toast.success(t.userCreated)
      await logAudit('CREATE_USER', t.auditUserCreated(userFormData.email), 'user')

      // ✅ Attribution automatique des permissions de saisie de notes (enseignants)
      const newUserId = created?.id || created?.data?.id
      if (newUserId) {
        const roleObj = roles.find(r => r.id.toString() === userFormData.role_id)
        await grantDefaultTeacherPermissions(Number(newUserId), roleObj?.name || selectedRoleName, false)
      }

      // Sauvegarder les assignations niveaux/classes pour surveillant/censeur
      if (newUserId && ['surveillant_general', 'censeur', 'surveillant'].includes(selectedRoleName.toLowerCase())) {
        if (assignedClassIds.length > 0 || assignedLevelIds.length > 0) {
          const saved = await saveUserAssignments(newUserId, assignedLevelIds, assignedClassIds)
          if (saved) {
            toast.success(lang === 'fr' ? 'Assignations enregistrees' : 'Assignments saved')
          }
        }
      }

      setShowUserModal(false)
      resetUserForm()
      loadUsers()
    } catch (err: any) { toast.error(err?.response?.data?.detail || t.errorCreate) }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canUpdateUser) { setAccessDeniedMessage(t.updateDenied); setShowAccessDeniedModal(true); return }
    if (!editingUser) return
    const errors: Record<string, string> = {}
    if (!userFormData.first_name.trim()) errors.first_name = t.firstNameRequired
    if (!userFormData.last_name.trim()) errors.last_name = t.lastNameRequired
    if (!userFormData.email.trim()) errors.email = t.emailRequired
    if (!userFormData.role_id) errors.role_id = t.roleRequired
    if (!userFormData.school_id) errors.school_id = t.schoolRequired
    if (!userFormData.academic_year_id) errors.academic_year_id = t.yearRequired
    setUserFormErrors(errors)
    if (Object.keys(errors).length > 0) return
    try {
      const updatePayload: any = {
        first_name: userFormData.first_name, last_name: userFormData.last_name,
        email: userFormData.email, phone: userFormData.phone, status: userFormData.status,
        role_id: parseInt(userFormData.role_id), school_id: parseInt(userFormData.school_id),
        academic_year_id: parseInt(userFormData.academic_year_id),
      }
      if (userFormData.password && userFormData.password.trim().length > 0) { updatePayload.password = userFormData.password }
      await authApi.updateUser(editingUser.id, updatePayload)
      toast.success(t.userUpdated)
      await logAudit('UPDATE_USER', t.auditUserUpdated(editingUser.email), 'user', editingUser.id)

      // Sauvegarder les assignations niveaux/classes séparément pour surveillant/censeur
      if (['surveillant_general', 'censeur', 'surveillant'].includes(selectedRoleName.toLowerCase())) {
        if (assignedClassIds.length > 0 || assignedLevelIds.length > 0) {
          const saved = await saveUserAssignments(editingUser.id, assignedLevelIds, assignedClassIds)
          if (saved) {
            toast.success(lang === 'fr' ? 'Assignations mises a jour' : 'Assignments updated')
          }
        } else {
          // Si aucune classe assignée, on supprime toutes les assignations existantes
          await saveUserAssignments(editingUser.id, [], [])
        }
      } else {
        // Si le role n'est plus surveillant/censeur, supprimer les assignations
        await saveUserAssignments(editingUser.id, [], [])
      }

      // ✅ S'assurer que les enseignants ont tjs leurs permissions de saisie
      // (merge : ne pas écraser leurs autres permissions)
      if (isTeacherRoleName(selectedRoleName)) {
        await grantDefaultTeacherPermissions(editingUser.id, selectedRoleName, true)
      }

      setShowUserModal(false)
      setEditingUser(null)
      loadUsers()
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.errorUpdate)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    }
  }

  const handleDeleteUser = async (id: number) => {
    if (!canDeleteUser) { setAccessDeniedMessage(t.deleteDenied); setShowAccessDeniedModal(true); return }
    if (!confirm(t.deleteConfirm)) return
    const userToDelete = users.find(u => u.id === id)
    try {
      await authApi.deleteUser(id)
      toast.success(t.userDeleted)
      if (userToDelete) {
        await logAudit('DELETE_USER', t.auditUserDeleted(userToDelete.email), 'user', id)
      }
      loadUsers()
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.errorDelete)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    }
  }

  const handleRemoveLevelAssignment = async (u: AppUser, levelId: number) => {
    if (!canUpdateUser) return
    const newLevelIds = (u.assigned_level_ids || []).filter(id => id !== levelId)
    const saved = await saveUserAssignments(u.id, newLevelIds, u.assigned_class_ids || [])
    if (saved) {
      toast.success(lang === 'fr' ? 'Niveau retiré' : 'Level removed')
      loadUsers()
    }
  }

  const handleToggleUserStatus = async (u: AppUser) => {
    if (!canUpdateUser) { setAccessDeniedMessage(t.updateDenied); setShowAccessDeniedModal(true); return }
    const newStatus = u.status === 'active' ? 'inactive' : 'active'
    try {
      await authApi.updateUser(u.id, { status: newStatus })
      toast.success(newStatus === 'active' ? t.userActivated : t.userDeactivated)
      await logAudit('UPDATE_USER_STATUS', t.auditUserStatus(u.email, newStatus), 'user', u.id)
      loadUsers()
    } catch (err: any) {
      const errorMsg = handleApiError(err, 'Erreur')
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    }
  }

  const open2FAModal = (u: AppUser) => {
    if (!canUpdateUser) { setAccessDeniedMessage(t.updateDenied); setShowAccessDeniedModal(true); return }
    setSelectedUserFor2FA(u)
    setShow2FAModal(true)
  }

  const handleToggleApp2FA = async () => {
    if (!selectedUserFor2FA || !canUpdateUser) return
    setToggling2FA(true)
    try {
      if (selectedUserFor2FA.is_2fa_enabled) {
        await authApi.adminDisable2FA(selectedUserFor2FA.id)
        toast.success(t.twofaDisabled)
        await logAudit('DISABLE_2FA', t.auditUser2FA(selectedUserFor2FA.email, false), 'user', selectedUserFor2FA.id)
      } else {
        await authApi.adminEnable2FA(selectedUserFor2FA.id)
        toast.success(t.twofaEnabled)
        await logAudit('ENABLE_2FA', t.auditUser2FA(selectedUserFor2FA.email, true), 'user', selectedUserFor2FA.id)
      }
      await loadUsers()
      const updatedUser = users.find(u => u.id === selectedUserFor2FA.id)
      if (updatedUser) setSelectedUserFor2FA(updatedUser)
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.error2FA)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    } finally {
      setToggling2FA(false)
    }
  }

  const handleToggleEmail2FA = async () => {
    if (!selectedUserFor2FA || !canUpdateUser) return
    setToggling2FA(true)
    const enable = !selectedUserFor2FA.email_2fa_enabled

    try {
      // ── V-FIX : endpoints via apiService (token JWT + baseURL corrects).
      // Ordre : methode dediee authApi -> POST email-2fa -> PUT user update.
      let saved = false
      let lastError: any = null

      // Essai 1 : methode dediee authApi si disponible
      try {
        if (enable && typeof (authApi as any).adminEnableEmail2FA === 'function') {
          await (authApi as any).adminEnableEmail2FA(selectedUserFor2FA.id)
          saved = true
        } else if (!enable && typeof (authApi as any).adminDisableEmail2FA === 'function') {
          await (authApi as any).adminDisableEmail2FA(selectedUserFor2FA.id)
          saved = true
        }
      } catch (err: any) { lastError = err }

      // Essai 2 : POST /auth/users/{id}/email-2fa/enable|disable
      if (!saved) {
        try {
          const endpoint = enable
            ? `/auth/users/${selectedUserFor2FA.id}/email-2fa/enable`
            : `/auth/users/${selectedUserFor2FA.id}/email-2fa/disable`
          await apiService.post(endpoint, {})
          saved = true
        } catch (err: any) { lastError = err }
      }

      // Essai 3 : PUT /auth/users/{id} avec le flag
      if (!saved) {
        try {
          await apiService.put(`/auth/users/${selectedUserFor2FA.id}`, { email_2fa_enabled: enable })
          saved = true
        } catch (err: any) { lastError = err }
      }

      if (saved) {
        toast.success(enable ? t.twofaEnabled : t.twofaDisabled)
        await logAudit(enable ? 'ENABLE_EMAIL_2FA' : 'DISABLE_EMAIL_2FA', t.auditUser2FA(selectedUserFor2FA.email, enable), 'user', selectedUserFor2FA.id)
        await loadUsers()
        const updatedUser = users.find(u => u.id === selectedUserFor2FA.id)
        if (updatedUser) setSelectedUserFor2FA(updatedUser)
      } else {
        throw lastError || new Error('Failed to update email 2FA')
      }
    } catch (err: any) {
      const errorMsg = handleApiError(err, t.error2FAEmail || t.error2FA)
      if (err?.response?.status === 403) { setAccessDeniedMessage(errorMsg); setShowAccessDeniedModal(true) }
      else { toast.error(errorMsg) }
    } finally {
      setToggling2FA(false)
    }
  }

  const openEditUser = (u: AppUser) => {
    if (!canUpdateUser) { setAccessDeniedMessage(t.updateDenied); setShowAccessDeniedModal(true); return }
    setEditingUser(u)
    const role = roles.find(r => r.id === u.role_id)
    setSelectedRoleName(role?.name || '')
    if (academicYears.length === 0 && u.school_id) loadAcademicYears(u.school_id)
    if (u.school_id) {
      loadLevels(u.school_id)
      loadClasses(u.school_id)
    }
    setUserFormData({
      first_name: u.first_name, last_name: u.last_name, email: u.email, phone: u.phone || '', password: '',
      role_id: u.role_id?.toString() || '', school_id: u.school_id?.toString() || '',
      academic_year_id: u.academic_year_id?.toString() || '', matricule: u.matricule || '', status: u.status,
    })
    setUserFormErrors({})
    // Toujours charger les assignations depuis l'API pour les surveillants/censeurs
    if (role && ['surveillant_general', 'censeur', 'surveillant'].includes(role.name.toLowerCase())) {
      loadUserAssignments(u.id)
    } else {
      setAssignedLevelIds([])
      setAssignedClassIds([])
    }
    setShowUserModal(true)
  }

  const openCreateUser = () => {
    if (!canCreateUser) { setAccessDeniedMessage(t.createDenied); setShowAccessDeniedModal(true); return }
    setEditingUser(null)
    resetUserForm()
    setSelectedRoleName('')
    setAssignedLevelIds([])
    setAssignedClassIds([])
    const currentSchoolId = currentUserSchoolId?.toString() || (schools.length > 0 ? schools[0].id.toString() : '')
    const currentYearId = activeAcademicYear?.id?.toString() || ''
    setUserFormData(prev => ({
      ...prev,
      school_id: currentSchoolId,
      academic_year_id: currentYearId,
    }))
    if (currentSchoolId) {
      loadAcademicYears(parseInt(currentSchoolId))
      loadLevels(parseInt(currentSchoolId))
      loadClasses(parseInt(currentSchoolId))
    }
    setShowUserModal(true)
  }

  const resetUserForm = () => {
    const currentSchoolId = currentUserSchoolId?.toString() || ''
    const currentYearId = activeAcademicYear?.id?.toString() || ''
    setUserFormData({
      first_name: '', last_name: '', email: '', phone: '', password: '',
      role_id: '', school_id: currentSchoolId, academic_year_id: currentYearId, matricule: '', status: 'active'
    })
    setUserFormErrors({})
  }

  const handleTogglePendingPermission = (permissionCode: string) => {
    if (!canManagePermissions) { setAccessDeniedMessage(t.managePermsDenied); setShowAccessDeniedModal(true); return }
    setPendingPermissions(prev => { const newState = { ...prev, [permissionCode]: !prev[permissionCode] }; setHasUnsavedPermissions(true); return newState })
  }

  const handleSavePermissions = async () => {
    if (!canManagePermissions) { setAccessDeniedMessage(t.managePermsDenied); setShowAccessDeniedModal(true); return }
    if (!selectedUserForPermissions) return

    const grantedPermissions = Object.entries(pendingPermissions)
      .filter(([, granted]) => granted)
      .map(([code]) => code)

    setSavingPermissions(true)

    // ── V-FIX 1 : n'envoyer QUE les codes connus du BACKEND LOCAL (table
    // permissions). Sinon le backend rejette TOUTE la requete avec un 400
    // "Permissions non reconnues et non créées: ...".
    let knownCodes: Set<string>
    try {
      const res: any = await apiService.get('/permissions')
      const list = Array.isArray(res) ? res : (res?.data || res?.items || [])
      knownCodes = new Set(list.map((p: any) => p.code || p.name).filter(Boolean))
    } catch {
      knownCodes = new Set(permissions.map(p => p.code).filter(Boolean))
    }
    const toSave = knownCodes.size > 0 ? grantedPermissions.filter(code => knownCodes.has(code)) : grantedPermissions
    const rejected = knownCodes.size > 0 ? grantedPermissions.filter(code => !knownCodes.has(code)) : []

    if (rejected.length > 0) {
      toast.warning(`${rejected.length} permission(s) inconnue(s) du backend, ignoree(s) : ${rejected.join(', ')}`)
      console.warn('[Permissions] Absentes de la table permissions :', rejected, '- executez migration_permissions_manquantes.sql puis rechargez la page.')
    }

    console.log('[Permissions] Saving for user', selectedUserForPermissions.id, ':', toSave)

    try {
      // ── V-FIX 2 : endpoint correct = PUT /auth/users/{id}/permissions via
      // apiService. Les anciens essais supprimes : adminApi (pointe vers la
      // PRODUCTION api.myhelperschool.cm), /admin/users/... (404) et fetch
      // manuel avec localStorage auth_token (JWT invalide -> 401).
      await apiService.put(`/auth/users/${selectedUserForPermissions.id}/permissions`, { permissions: toSave })
      console.log('[Permissions] Saved via PUT /auth/users/{id}/permissions')

      // Recharger l'etat REEL depuis le backend (pas ce qu'on a envoye)
      try {
        const res = await authApi.getUserPermissions(selectedUserForPermissions.id)
        const actualPerms: string[] = res?.permissions || []
        setUserPermissions(actualPerms)
        const pending: Record<string, boolean> = {}
        ALL_PERMISSION_CODES.forEach(code => { pending[code] = false })
        actualPerms.forEach((code: string) => { pending[code] = true })
        setPendingPermissions(pending)
        setHasUnsavedPermissions(false)

        const notSaved = toSave.filter(p => !actualPerms.includes(p))
        if (notSaved.length > 0) {
          toast.warning(`${notSaved.length} permission(s) refusee(s) par le backend : ${notSaved.join(', ')}`)
        } else {
          toast.success(t.permissionsSaved)
        }
      } catch (reloadErr) {
        console.error('[Permissions] Reload after save failed:', reloadErr)
        setUserPermissions(toSave)
        setHasUnsavedPermissions(false)
        toast.success(t.permissionsSaved)
      }
      await logAudit('UPDATE_PERMISSIONS', t.auditPermissionsUpdated(selectedUserForPermissions.email), 'user', selectedUserForPermissions.id)
    } catch (err: any) {
      console.error('[Permissions] Save failed:', err)
      toast.error(handleApiError(err, t.errorSavePermissions))
      if (err?.response) {
        console.error('Status:', err.response.status, '| Data:', err.response.data)
      }
    } finally {
      setSavingPermissions(false)
    }
  }

  const handleSelectUserForPermissions = (u: AppUser) => {
    if (!canManagePermissions) { setAccessDeniedMessage(t.managePermsDenied); setShowAccessDeniedModal(true); return }
    setSelectedUserForPermissions(u)
    setHasUnsavedPermissions(false)
    // Reset pending permissions while loading
    const pending: Record<string, boolean> = {}
    ALL_PERMISSION_CODES.forEach(code => { pending[code] = false })
    setPendingPermissions(pending)
  }

  const isPermissionGranted = (permissionCode: string): boolean => { return pendingPermissions[permissionCode] || false }
  const toggleModule = (moduleId: string) => { setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] })) }
  const getModuleProgress = (moduleId: string): { total: number; granted: number } => {
    const module = PERMISSION_MODULES.find(m => m.id === moduleId)
    if (!module) return { total: 0, granted: 0 }
    const total = module.permissions.length
    const granted = module.permissions.filter(p => pendingPermissions[p.code]).length
    return { total, granted }
  }

  const downloadExcelTemplate = () => {
    const currentSchoolId = currentUserSchoolId?.toString() || ''
    const currentYearId = activeAcademicYear?.id?.toString() || ''
    const headers = ['first_name', 'last_name', 'email', 'phone', 'password', 'role_id', 'school_id', 'academic_year_id', 'status', 'matricule']
    const exampleRow = ['Jean', 'Dupont', 'jean.dupont@example.com', '+237123456789', 'Password123!', '1', currentSchoolId, currentYearId, 'active', '']
    const instructions = [
      lang === 'fr' ? 'INSTRUCTIONS:' : 'INSTRUCTIONS:',
      lang === 'fr' ? '- role_id: ID du role (voir liste ci-dessous)' : '- role_id: Role ID (see list below)',
      lang === 'fr' ? "- school_id: ID de l'etablissement" : '- school_id: School ID',
      lang === 'fr' ? "- academic_year_id: ID de l'annee scolaire" : '- academic_year_id: Academic year ID',
      lang === 'fr' ? '- status: active, inactive, ou suspended' : '- status: active, inactive, or suspended',
      lang === 'fr' ? '- matricule: laisser vide pour auto-generation' : '- matricule: leave blank for auto-generation',
      lang === 'fr' ? '- password: min 6 caracteres, 1 majuscule, 1 minuscule, 1 chiffre, 1 special' : '- password: min 6 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special',
    ]
    const refData = [
      [lang === 'fr' ? 'ROLES DISPONIBLES:' : 'AVAILABLE ROLES:', ''],
      ...roles.map(r => [r.id.toString(), r.name]),
      [''],
      [lang === 'fr' ? 'ETABLISSEMENTS DISPONIBLES:' : 'AVAILABLE SCHOOLS:', ''],
      ...schools.map(s => [s.id.toString(), s.name]),
      [''],
      [lang === 'fr' ? 'ANNEES SCOLAIRES:' : 'ACADEMIC YEARS:', ''],
      ...academicYears.map(a => [a.id.toString(), a.label + (a.is_selected ? (lang === 'fr' ? ' (ACTIVE)' : ' (ACTIVE)') : '')]),
    ]
    const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow, [], ...instructions.map(i => [i]), [], ...refData])
    ws['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 20 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, lang === 'fr' ? 'Modele Utilisateurs' : 'Users Template')
    XLSX.writeFile(wb, lang === 'fr' ? 'modele_import_utilisateurs.xlsx' : 'users_import_template.xlsx')
    toast.success(t.templateDownloaded)
  }

  const handleExcelFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setExcelImportLoading(true)
    setExcelImportResults(null)
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = event.target?.result
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][]
        if (jsonData.length < 2) { toast.error(t.fileEmpty); setExcelImportLoading(false); return }
        const headers = jsonData[0] as string[]
        const rows = jsonData.slice(1)
        const mappedData: ExcelUserRow[] = []
        const errors: string[] = []
        rows.forEach((row, index) => {
          if (row.length === 0 || row.every(cell => !cell)) return
          const rowData: any = {}
          headers.forEach((header, i) => { rowData[header] = row[i] })
          const rowNum = index + 2
          if (!rowData.first_name) errors.push(`Ligne ${rowNum}: ${t.firstNameRequired}`)
          if (!rowData.last_name) errors.push(`Ligne ${rowNum}: ${t.lastNameRequired}`)
          if (!rowData.email) errors.push(`Ligne ${rowNum}: ${t.emailRequired}`)
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rowData.email)) errors.push(`Ligne ${rowNum}: ${t.emailInvalid}`)
          if (!rowData.password) errors.push(`Ligne ${rowNum}: ${t.passwordRequired}`)
          if (!rowData.role_id) errors.push(`Ligne ${rowNum}: ${t.roleRequired}`)
          if (!rowData.school_id) errors.push(`Ligne ${rowNum}: ${t.schoolRequired}`)
          if (!rowData.academic_year_id) errors.push(`Ligne ${rowNum}: ${t.yearRequired}`)
          const roleId = parseInt(rowData.role_id)
          const roleName = roles.find(r => r.id === roleId)?.name
          const matricule = rowData.matricule || generateMatricule(roleName)
          mappedData.push({
            first_name: String(rowData.first_name || ''), last_name: String(rowData.last_name || ''),
            email: String(rowData.email || ''), phone: rowData.phone ? String(rowData.phone) : undefined,
            password: String(rowData.password || ''), role_id: roleId, school_id: parseInt(rowData.school_id),
            academic_year_id: parseInt(rowData.academic_year_id), status: String(rowData.status || 'active'), matricule,
          })
        })
        if (errors.length > 0) { toast.error(`${errors.length} erreur(s) trouvee(s) dans le fichier`); console.error('Erreurs de validation:', errors) }
        setExcelPreviewData(mappedData)
        toast.success(t.readyToImport(mappedData.length))
      } catch (err) { console.error('Erreur lecture Excel:', err); toast.error(t.readError) }
      finally { setExcelImportLoading(false) }
    }
    reader.onerror = () => { toast.error(t.fileReadError); setExcelImportLoading(false) }
    reader.readAsArrayBuffer(file)
    if (fileInputRef.current) { fileInputRef.current.value = '' }
  }

  const handleImportUsers = async () => {
    if (!canCreateUser) { setAccessDeniedMessage(t.createDenied); setShowAccessDeniedModal(true); return }
    if (excelPreviewData.length === 0) { toast.error(t.noDataToImport); return }
    setExcelImportLoading(true)
    const results = { success: 0, errors: 0, details: [] as string[] }
    for (const userData of excelPreviewData) {
      try {
        const created: any = await authApi.createUser({
          first_name: userData.first_name, last_name: userData.last_name, email: userData.email,
          phone: userData.phone, password: userData.password, role_id: userData.role_id,
          school_id: userData.school_id, academic_year_id: userData.academic_year_id,
          matricule: userData.matricule, status: userData.status,
        })
        // ✅ Permissions de saisie de notes pour les lignes "enseignant"
        const createdId = created?.id || created?.data?.id
        if (createdId) {
          const roleObj = roles.find(r => r.id === userData.role_id)
          await grantDefaultTeacherPermissions(Number(createdId), roleObj?.name || '', false)
        }
        results.success++; results.details.push(`✅ ${userData.email} - ${lang === 'fr' ? 'Cree' : 'Created'}`)
      } catch (err: any) {
        results.errors++
        const errorMsg = err?.response?.data?.detail || err?.message || (lang === 'fr' ? 'Erreur inconnue' : 'Unknown error')
        results.details.push(`❌ ${userData.email} - ${errorMsg}`)
      }
    }
    if (results.success > 0) {
      await logAudit('IMPORT_USERS', t.auditImport(results.success), 'user')
    }
    setExcelImportResults(results)
    setExcelImportLoading(false)
    if (results.success > 0) { toast.success(t.importedSuccess(results.success)); loadUsers() }
    if (results.errors > 0) { toast.error(t.importErrors(results.errors)) }
  }

  const clearExcelPreview = () => { setExcelPreviewData([]); setExcelImportResults(null) }

  const getStatusBadge = (status: string) => {
    const statusConfig = USER_STATUSES.find(s => s.value === status)
    const label = statusConfig?.label[lang] || status
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig?.color || 'bg-gray-100 text-gray-700'}`}>{label}</span>
  }

  const filteredUsers = useMemo(() => {
    let result = users

    // Filtre par recherche textuelle
    if (userSearch) {
      const search = userSearch.toLowerCase()
      result = result.filter(u => 
        u.first_name.toLowerCase().includes(search) || 
        u.last_name.toLowerCase().includes(search) || 
        u.email.toLowerCase().includes(search) || 
        (u.matricule || '').toLowerCase().includes(search) || 
        (u.role_name || '').toLowerCase().includes(search)
      )
    }

    // Filtre par rôle
    if (selectedRoleFilter) {
      result = result.filter(u => u.role_id?.toString() === selectedRoleFilter)
    }

    // Filtre par statut
    if (selectedStatusFilter) {
      result = result.filter(u => u.status === selectedStatusFilter)
    }

    return result
  }, [users, userSearch, selectedRoleFilter, selectedStatusFilter])

  return (
    <div className="animate-fade-in space-y-6">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Users className="h-7 w-7 text-primary-600" />
              {t.pageTitle}
            </h1>
            <p className="page-subtitle">{t.pageSubtitle}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={toggleLang}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              title={lang === 'fr' ? 'Switch to English' : 'Passer en francais'}
            >
              <Globe className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{lang === 'fr' ? 'FR' : 'EN'}</span>
            </button>
            {activeYearLoading ? (
              <span className="text-xs text-gray-400 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />{t.loadingYear}</span>
            ) : activeAcademicYear ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg">
                <GraduationCap className="w-4 h-4 text-primary-600" />
                <span className="text-sm font-medium text-primary-700">{activeAcademicYear.label}</span>
                <span className="text-xs text-primary-500">({t.activeYear})</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-amber-700">{t.noActiveYear}</span>
              </div>
            )}
            {!canViewAllUsers && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg" title={t.filteredViewDesc(currentUserSchoolId || '?')}>
                <EyeOff className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-700">{t.filteredView}</span>
              </div>
            )}
            {canViewAllUsers && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg" title={lang === 'fr' ? 'Vous voyez tous les utilisateurs' : 'You see all users'}>
                <Eye className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-700">{t.fullView}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1 w-fit shadow-sm">
        <button onClick={() => setViewMode('users')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'users' ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
          <Users className="w-4 h-4" />{t.usersTab}
        </button>
        {canCreateUser && (
          <button onClick={() => setViewMode('import')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'import' ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Upload className="w-4 h-4" />{t.importTab}
          </button>
        )}
        {canManagePermissions && (
          <button onClick={() => setViewMode('permissions')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'permissions' ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
            <ShieldCheck className="w-4 h-4" />{t.permissionsTab}
          </button>
        )}
      </div>

      {viewMode === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder={t.searchPlaceholder} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="input pl-10 w-full" />
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`btn-outline whitespace-nowrap flex items-center gap-2 ${showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : ''}`}
              >
                <Filter className="w-4 h-4" />
                {lang === 'fr' ? 'Plus de filtres' : 'More filters'}
                {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {canCreateUser && <button onClick={() => setViewMode('import')} className="btn-outline whitespace-nowrap"><Upload className="mr-2 h-4 w-4" />{t.importTab}</button>}
              {canCreateUser && <button onClick={openCreateUser} className="btn-primary whitespace-nowrap"><UserPlus className="mr-2 h-4 w-4" />{t.newUser}</button>}
            </div>
          </div>

          {/* PLUS DE FILTRES */}
          {showFilters && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                {/* Filtre par établissement */}
                <div className="flex-1 min-w-[200px]">
                  <label className="label text-xs text-gray-500 mb-1">{lang === 'fr' ? 'Établissement' : 'School'}</label>
                  <select 
                    value={selectedSchoolFilter !== null ? selectedSchoolFilter : ''} 
                    onChange={(e) => {
                      const val = e.target.value
                      const parsed = val ? parseInt(val, 10) : null
                      console.log('[UsersPage] School filter changed:', val, '→ parsed:', parsed)
                      setSelectedSchoolFilter(parsed)
                      setUserPage(1)
                    }}
                    className="input w-full"
                    disabled={schoolsLoading || schools.length === 0}
                  >
                    <option value="">{schoolsLoading ? (lang === 'fr' ? 'Chargement...' : 'Loading...') : (lang === 'fr' ? 'Tous les établissements' : 'All schools')}</option>
                    {schools.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} (ID: {s.id})</option>
                    ))}
                  </select>
                  {schools.length === 0 && !schoolsLoading && (
                    <p className="text-xs text-red-500 mt-1">{lang === 'fr' ? 'Aucun établissement chargé' : 'No schools loaded'}</p>
                  )}
                </div>

                {/* Filtre par rôle */}
                <div className="flex-1 min-w-[200px]">
                  <label className="label text-xs text-gray-500 mb-1">{lang === 'fr' ? 'Rôle' : 'Role'}</label>
                  <select 
                    value={selectedRoleFilter} 
                    onChange={(e) => {
                      setSelectedRoleFilter(e.target.value)
                      setUserPage(1)
                    }}
                    className="input w-full"
                  >
                    <option value="">{lang === 'fr' ? 'Tous les rôles' : 'All roles'}</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                {/* Filtre par statut */}
                <div className="flex-1 min-w-[200px]">
                  <label className="label text-xs text-gray-500 mb-1">{lang === 'fr' ? 'Statut' : 'Status'}</label>
                  <select 
                    value={selectedStatusFilter} 
                    onChange={(e) => {
                      setSelectedStatusFilter(e.target.value)
                      setUserPage(1)
                    }}
                    className="input w-full"
                  >
                    <option value="">{lang === 'fr' ? 'Tous les statuts' : 'All statuses'}</option>
                    {USER_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label[lang]}</option>
                    ))}
                  </select>
                </div>

                {/* Bouton réinitialiser */}
                <button 
                  onClick={() => {
                    setSelectedSchoolFilter(null)
                    setSelectedRoleFilter('')
                    setSelectedStatusFilter('')
                    setUserPage(1)
                  }}
                  className="btn-outline text-sm whitespace-nowrap"
                >
                  <X className="w-3 h-3 mr-1" />
                  {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
                </button>
              </div>

              {/* Badge filtre actif */}
              {(selectedSchoolFilter !== null || selectedRoleFilter || selectedStatusFilter) && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                  {selectedSchoolFilter !== null && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                      {lang === 'fr' ? 'Établissement' : 'School'}: {schools.find(s => s.id === selectedSchoolFilter)?.name || `ID:${selectedSchoolFilter}`}
                      <button onClick={() => { console.log('[UsersPage] Clearing school filter'); setSelectedSchoolFilter(null) }} className="hover:text-primary-900"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {selectedRoleFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                      {lang === 'fr' ? 'Rôle' : 'Role'}: {roles.find(r => r.id.toString() === selectedRoleFilter)?.name || selectedRoleFilter}
                      <button onClick={() => setSelectedRoleFilter('')} className="hover:text-primary-900"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {selectedStatusFilter && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                      {lang === 'fr' ? 'Statut' : 'Status'}: {USER_STATUSES.find(s => s.value === selectedStatusFilter)?.label[lang] || selectedStatusFilter}
                      <button onClick={() => setSelectedStatusFilter('')} className="hover:text-primary-900"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {!activeAcademicYear && !activeYearLoading && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">{t.noActiveYear}</p>
                <p className="text-xs text-amber-600">{t.noActiveYearDesc}</p>
              </div>
            </div>
          )}

          {!canViewAllUsers && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
              <EyeOff className="w-5 h-5 text-blue-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">{t.filteredView}</p>
                <p className="text-xs text-blue-600">{t.filteredViewDesc(currentUserSchoolId || '?')}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {usersLoading ? (
              <div className="col-span-full flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-primary-300" /></div>
            ) : !canReadUsers ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400"><Lock className="w-12 h-12 mb-3 opacity-30" /><p className="text-sm">{t.restrictedAccess}</p></div>
            ) : filteredUsers.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                <Users className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">{t.noUsers}</p>
                {activeAcademicYear && <p className="text-xs text-gray-400 mt-1">{t.activeYear}: {activeAcademicYear.label}</p>}
                {!canViewAllUsers && <p className="text-xs text-gray-400 mt-1">{t.filterApplied(currentUserSchoolId || '?')}</p>}
              </div>
            ) : (
              filteredUsers.map((u) => (
                <div key={u.id} className="card hover:shadow-lg transition-all duration-200 border border-gray-100">
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-sm font-bold text-primary-700">{u.first_name[0]}{u.last_name[0]}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-gray-900 truncate">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                          <p className="text-xs font-mono text-gray-400 mt-0.5">{u.matricule || '-'}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {getStatusBadge(u.status)}
                        <span className="text-xs text-gray-500 font-medium">{u.role_name || '-'}</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><School className="w-3 h-3" />{u.school_name || '-'}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{u.academic_year_label || '-'}</span>
                        </div>
                        {/* Affichage des niveaux/classes assignes */}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {u.assigned_level_ids?.map((lid) => {
                            const levelName = levels.find((l) => l.id === lid)?.name || `Niveau ${lid}`
                            return (
                              <span key={`lvl-${lid}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium border border-blue-100">
                                <GraduationCap className="w-3 h-3" />
                                {levelName}
                              </span>
                            )
                          })}
                          {u.assigned_class_ids?.map((cid) => {
                            const clsName = classes.find((c) => c.id === cid)?.name || `Classe ${cid}`
                            return (
                              <span key={`cls-${cid}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-[10px] font-medium border border-green-100">
                                <School className="w-3 h-3" />
                                {clsName}
                                {canUpdateUser && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation()
                                      const newIds = (u.assigned_class_ids || []).filter(id => id !== cid)
                                      const saved = await saveUserAssignments(u.id, u.assigned_level_ids || [], newIds)
                                      if (saved) {
                                        toast.success(lang === 'fr' ? `Classe ${clsName} retirée` : `Class ${clsName} removed`)
                                        loadUsers()
                                      }
                                    }}
                                    className="ml-0.5 hover:text-red-600"
                                    title={lang === 'fr' ? 'Retirer cette classe' : 'Remove this class'}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </span>
                            )
                          })}
                        </div>
                        <div className="flex items-center gap-0.5">
                          {canUpdateUser && (
                            <button onClick={() => open2FAModal(u)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title={lang === 'fr' ? 'Gerer la 2FA' : 'Manage 2FA'}>
                              {(u.is_2fa_enabled || u.email_2fa_enabled) ? <ShieldCheck className="w-4 h-4 text-green-500" /> : <ShieldOff className="w-4 h-4 text-gray-300" />}
                            </button>
                          )}
                          {canUpdateUser && (
                            <button onClick={() => handleToggleUserStatus(u)} className={`p-1.5 hover:bg-gray-100 rounded-lg transition-colors ${u.status === 'active' ? 'text-green-600' : 'text-gray-400'}`} title={u.status === 'active' ? (lang === 'fr' ? 'Desactiver' : 'Deactivate') : (lang === 'fr' ? 'Activer' : 'Activate')}>
                              {u.status === 'active' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                          )}
                          {canUpdateUser && (
                            <button onClick={() => openEditUser(u)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title={lang === 'fr' ? 'Modifier' : 'Edit'}><Edit2 className="w-4 h-4" /></button>
                          )}
                          {canDeleteUser && (
                            <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition-colors" title={lang === 'fr' ? 'Supprimer' : 'Delete'}><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {userTotal > 20 && canReadUsers && (
            <div className="flex items-center justify-between text-sm text-gray-500 pt-2">
              <span>{t.total}: {userTotal} {filteredUsers.length !== userTotal && <span className="text-xs text-gray-400">({filteredUsers.length} {t.displayed})</span>}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setUserPage(p => Math.max(1, p - 1))} disabled={userPage === 1} className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">{t.previous}</button>
                <span className="px-2 font-medium">{t.page} {userPage}</span>
                <button onClick={() => setUserPage(p => p + 1)} disabled={userPage * 20 >= userTotal} className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">{t.next}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'import' && canCreateUser && (
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold flex items-center gap-2"><FileDown className="w-5 h-5 text-primary-600" />{t.step1}</h3>
            </div>
            <div className="card-body">
              <p className="text-sm text-gray-600 mb-4">{t.step1Desc}</p>
              <div className="flex items-center gap-4">
                <button onClick={downloadExcelTemplate} className="btn-primary flex items-center gap-2"><Download className="w-4 h-4" />{t.downloadTemplate}</button>
                <div className="text-xs text-gray-500">
                  <p>{t.requiredCols}</p>
                  <p>{t.optionalCols}</p>
                </div>
              </div>
              {activeAcademicYear && (
                <div className="mt-3 p-2 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-xs text-primary-700 flex items-center gap-1"><GraduationCap className="w-3 h-3" />{t.usersCreatedFor} <strong>{activeAcademicYear.label}</strong></p>
                </div>
              )}
              {!canViewAllUsers && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 flex items-center gap-1"><EyeOff className="w-3 h-3" />{t.usersCreatedForSchool(currentUserSchoolId || '?')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold flex items-center gap-2"><Upload className="w-5 h-5 text-primary-600" />{t.step2}</h3>
            </div>
            <div className="card-body">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary-400 transition-colors">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-2">{t.dropFile}</p>
                <p className="text-xs text-gray-400 mb-4">{t.acceptedFormat}</p>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelFileUpload} className="hidden" id="excel-upload" />
                <label htmlFor="excel-upload" className="btn-primary inline-flex items-center gap-2 cursor-pointer"><Upload className="w-4 h-4" />{t.chooseFile}</label>
              </div>
              {excelImportLoading && (
                <div className="flex items-center justify-center py-4"><RefreshCw className="w-6 h-6 animate-spin text-primary-400" /><span className="ml-2 text-sm text-gray-600">{t.processing}</span></div>
              )}
            </div>
          </div>

          {excelPreviewData.length > 0 && (
            <div className="card">
              <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="font-semibold flex items-center gap-2"><Table2 className="w-5 h-5 text-primary-600" />{t.previewUsers(excelPreviewData.length)}</h3>
                <div className="flex items-center gap-2">
                  <button onClick={clearExcelPreview} className="btn-outline text-sm"><X className="w-3.5 h-3.5 mr-1" />{t.cancel}</button>
                  <button onClick={handleImportUsers} disabled={excelImportLoading} className="btn-primary text-sm">
                    {excelImportLoading ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />{t.importInProgress}</> : <><Upload className="w-3.5 h-3.5 mr-1" />{t.importUsers(excelPreviewData.length)}</>}
                  </button>
                </div>
              </div>
              <div className="card-body overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.firstName}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.lastName}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.email}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.phone}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.roleId}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.schoolId}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.yearId}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.status}</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">{t.matricule}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelPreviewData.map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3">{row.first_name}</td>
                        <td className="py-2 px-3">{row.last_name}</td>
                        <td className="py-2 px-3 text-primary-600">{row.email}</td>
                        <td className="py-2 px-3">{row.phone || '-'}</td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{row.role_id}</span></td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{row.school_id}</span></td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{row.academic_year_id}</span></td>
                        <td className="py-2 px-3">{getStatusBadge(row.status)}</td>
                        <td className="py-2 px-3 font-mono text-xs">{row.matricule}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {excelImportResults && (
            <div className={`card ${excelImportResults.errors > 0 ? 'border-amber-200' : 'border-green-200'}`}>
              <div className="card-header">
                <h3 className={`font-semibold flex items-center gap-2 ${excelImportResults.errors > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                  {excelImportResults.errors > 0 ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}{t.importResults}
                </h3>
              </div>
              <div className="card-body">
                <div className="flex items-center gap-6 mb-4">
                  <div className="text-center"><p className="text-2xl font-bold text-green-600">{excelImportResults.success}</p><p className="text-xs text-gray-500">{t.success}</p></div>
                  <div className="text-center"><p className="text-2xl font-bold text-red-600">{excelImportResults.errors}</p><p className="text-xs text-gray-500">{t.errors}</p></div>
                </div>
                {excelImportResults.details.length > 0 && (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {excelImportResults.details.map((detail, idx) => (
                      <p key={idx} className={`text-xs py-0.5 ${detail.startsWith('✅') ? 'text-green-700' : 'text-red-700'}`}>{detail}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'permissions' && canManagePermissions && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="card">
              <div className="card-header">
                <h3 className="font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-primary-600" />{t.selectUser}</h3>
              </div>
              <div className="card-body">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder={t.search} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="input pl-10 w-full" />
                </div>
                {usersLoading ? (
                  <div className="flex justify-center py-6"><RefreshCw className="w-5 h-5 animate-spin text-gray-300" /></div>
                ) : (
                  <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1">
                    {filteredUsers.map((u) => (
                      <button key={u.id} onClick={() => handleSelectUserForPermissions(u)} className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${selectedUserForPermissions?.id === u.id ? 'bg-primary-50 border border-primary-200 shadow-sm' : 'hover:bg-gray-50 border border-transparent'}`}>
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary-600">{u.first_name[0]}{u.last_name[0]}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{u.first_name} {u.last_name}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                        {selectedUserForPermissions?.id === u.id && <CheckCircle className="w-4 h-4 text-primary-600 shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {selectedUserForPermissions ? (
              <div className="card">
                <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary-600" />{t.userPermissions(`${selectedUserForPermissions.first_name} ${selectedUserForPermissions.last_name}`)}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{selectedUserForPermissions.email} · {selectedUserForPermissions.role_name || t.noRole}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasUnsavedPermissions && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">{t.unsavedChanges}</span>}
                    <button onClick={handleSavePermissions} disabled={savingPermissions || !hasUnsavedPermissions} className="btn-primary text-sm">
                      {savingPermissions ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />{t.saving}</> : <><Save className="w-3.5 h-3.5 mr-1" />{t.save}</>}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input type="text" placeholder={t.searchPermission} value={permissionSearch} onChange={(e) => setPermissionSearch(e.target.value)} className="input pl-10 w-full" />
                  </div>
                  {permissionsLoading ? (
                    <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-300" /></div>
                  ) : (
                    <div className="space-y-2">
                      {PERMISSION_MODULES.map((module) => {
                        const progress = getModuleProgress(module.id)
                        const filteredPerms = module.permissions.filter(p => permissionSearch === '' || p.label[lang].toLowerCase().includes(permissionSearch.toLowerCase()) || p.code.toLowerCase().includes(permissionSearch.toLowerCase()))
                        if (filteredPerms.length === 0) return null
                        return (
                          <div key={module.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                            <button onClick={() => toggleModule(module.id)} className="w-full flex items-center justify-between p-3.5 bg-gray-50/80 hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-2.5">
                                <span className="text-sm font-semibold text-gray-800">{module.label[lang]}</span>
                                <span className="text-xs text-gray-400 font-medium">({progress.granted}/{progress.total})</span>
                              </div>
                              <div className="flex items-center gap-2.5">
                                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full bg-primary-500 rounded-full transition-all duration-300" style={{ width: progress.total > 0 ? `${(progress.granted / progress.total) * 100}%` : '0%' }} />
                                </div>
                                {expandedModules[module.id] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                              </div>
                            </button>
                            {expandedModules[module.id] && (
                              <div className="p-3 space-y-1">
                                {filteredPerms.map((perm) => {
                                  const granted = isPermissionGranted(perm.code)
                                  return (
                                    <div key={perm.code} className="flex items-center justify-between p-2.5 hover:bg-gray-50 rounded-lg transition-colors">
                                      <div className="flex items-center gap-3">
                                        <button onClick={() => handleTogglePendingPermission(perm.code)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${granted ? 'bg-primary-600 border-primary-600' : 'border-gray-300 hover:border-primary-400'}`}>
                                          {granted && <Check className="w-3 h-3 text-white" />}
                                        </button>
                                        <span className="text-sm text-gray-700">{perm.label[lang]}</span>
                                      </div>
                                      <code className="text-[11px] text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{perm.code}</code>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body flex flex-col items-center justify-center py-16 text-gray-400">
                  <ShieldCheck className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-sm font-medium">{t.selectUserHint}</p>
                  <p className="text-xs mt-1">{t.selectUserHintDesc}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showUserModal && (canCreateUser || (editingUser && canUpdateUser)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowUserModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{editingUser ? t.editUserTitle : t.newUserTitle}</h3>
              <button onClick={() => setShowUserModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="label">{t.matriculeLabel} <span className="text-xs text-gray-400 font-normal">({t.autoGenerated})</span></label>
                <div className="flex items-center gap-2">
                  <input value={userFormData.matricule} readOnly className="input bg-gray-50 text-gray-600 font-mono text-sm" placeholder={t.select} />
                  {!editingUser && userFormData.matricule && <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />}
                </div>
                {userFormErrors.matricule && <p className="text-xs text-red-500 mt-1">{userFormErrors.matricule}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.firstNameLabel} *</label>
                  <input required value={userFormData.first_name} onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })} className="input" />
                  {userFormErrors.first_name && <p className="text-xs text-red-500 mt-1">{userFormErrors.first_name}</p>}
                </div>
                <div>
                  <label className="label">{t.lastNameLabel} *</label>
                  <input required value={userFormData.last_name} onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })} className="input" />
                  {userFormErrors.last_name && <p className="text-xs text-red-500 mt-1">{userFormErrors.last_name}</p>}
                </div>
              </div>
              <div>
                <label className="label">{t.emailLabel} *</label>
                <input type="email" required value={userFormData.email} onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })} className="input" />
                {userFormErrors.email && <p className="text-xs text-red-500 mt-1">{userFormErrors.email}</p>}
              </div>
              <div>
                <label className="label">{t.phoneLabel}</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input value={userFormData.phone} onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })} className="input pl-10" />
                </div>
              </div>
              <div>
                <label className="label">{editingUser ? t.passwordEditHint : `${t.passwordLabel} *`}</label>
                <div className="relative">
                  <input type={showNewPassword ? 'text' : 'password'} required={!editingUser} value={userFormData.password} onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })} className="input pr-10" />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><KeyRound className="w-4 h-4" /></button>
                </div>
                {userFormErrors.password && <p className="text-xs text-red-500 mt-1">{userFormErrors.password}</p>}
                <p className="text-xs text-gray-400 mt-1">{t.passwordHint}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.roleLabel} *</label>
                  <select required value={userFormData.role_id} onChange={(e) => handleRoleChange(e.target.value)} className="input">
                    <option value="">{t.select}</option>
                    {roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                  </select>
                  {userFormErrors.role_id && <p className="text-xs text-red-500 mt-1">{userFormErrors.role_id}</p>}
                </div>
                <div>
                  <label className="label">{t.statusLabel}</label>
                  <select value={userFormData.status} onChange={(e) => setUserFormData({ ...userFormData, status: e.target.value })} className="input">
                    {USER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label[lang]}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.schoolLabel} *</label>
                  <select required value={userFormData.school_id} onChange={(e) => { 
                    setUserFormData({ ...userFormData, school_id: e.target.value, academic_year_id: '' }); 
                    if (e.target.value) {
                      loadAcademicYears(parseInt(e.target.value))
                      loadLevels(parseInt(e.target.value))
                      loadClasses(parseInt(e.target.value))
                    }
                  }} className="input">
                    <option value="">{t.select}</option>
                    {schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
                  </select>
                  {userFormErrors.school_id && <p className="text-xs text-red-500 mt-1">{userFormErrors.school_id}</p>}
                </div>
                <div>
                  <label className="label">{t.academicYearLabel} *</label>
                  <select required value={userFormData.academic_year_id} onChange={(e) => setUserFormData({ ...userFormData, academic_year_id: e.target.value })} className="input">
                    <option value="">{t.select}</option>
                    {academicYears.map((year) => <option key={year.id} value={year.id}>{year.label}{year.is_selected ? (lang === 'fr' ? ' (ACTIVE)' : ' (ACTIVE)') : ''}</option>)}
                  </select>
                  {userFormErrors.academic_year_id && <p className="text-xs text-red-500 mt-1">{userFormErrors.academic_year_id}</p>}
                </div>
              </div>

              {/* ===== SECTION NIVEAUX ET CLASSES (SURVEILLANT GENERAL / CENSEUR) ===== */}
              {(['surveillant_general', 'censeur', 'surveillant'].includes(selectedRoleName.toLowerCase())) && (
                <div className="col-span-2 border border-gray-200 rounded-xl p-4 bg-gray-50/50 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-primary-600" />
                    {t.levelsAndClasses}
                  </h4>

                  {/* Niveaux */}
                  <div>
                    <label className="label text-xs text-gray-500 mb-2">{t.selectLevels}</label>
                    {levels.length === 0 ? (
                      <p className="text-xs text-gray-400">{t.noLevels}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {levels.map((level) => (
                          <button
                            key={level.id}
                            type="button"
                            onClick={() => {
                              setAssignedLevelIds((prev) =>
                                prev.includes(level.id)
                                  ? prev.filter((id) => id !== level.id)
                                  : [...prev, level.id]
                              )
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              assignedLevelIds.includes(level.id)
                                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                            }`}
                          >
                            {level.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Classes par niveau */}
                  {assignedLevelIds.length > 0 && (
                    <div>
                      <label className="label text-xs text-gray-500 mb-2">{t.selectClasses}</label>
                      <div className="space-y-3">
                        {assignedLevelIds.map((levelId) => {
                          const level = levels.find((l) => l.id === levelId)
                          const levelClasses = classes.filter((c) => c.level_id === levelId)
                          if (!level || levelClasses.length === 0) return null
                          return (
                            <div key={levelId} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <p className="text-xs font-semibold text-gray-700 mb-2">{level.name}</p>
                              <div className="flex flex-wrap gap-2">
                                {levelClasses.map((cls) => (
                                  <label
                                    key={cls.id}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border cursor-pointer transition-all ${
                                      assignedClassIds.includes(cls.id)
                                        ? 'bg-blue-50 text-blue-700 border-blue-300'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={assignedClassIds.includes(cls.id)}
                                      onChange={(e) => {
                                        setAssignedClassIds((prev) =>
                                          e.target.checked
                                            ? [...prev, cls.id]
                                            : prev.filter((id) => id !== cls.id)
                                        )
                                      }}
                                      className="w-3.5 h-3.5 text-primary-600 rounded"
                                    />
                                    {cls.name}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {assignmentsLoading && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      {lang === 'fr' ? 'Chargement des assignations...' : 'Loading assignments...'}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowUserModal(false)} className="btn-outline">{t.cancelBtn}</button>
                <button type="submit" className="btn-primary">{editingUser ? t.updateBtn : t.createBtn}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {show2FAModal && selectedUserFor2FA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShow2FAModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary-600" />
                {t.twoFAManagement}
              </h3>
              <button onClick={() => setShow2FAModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary-700">{selectedUserFor2FA.first_name[0]}{selectedUserFor2FA.last_name[0]}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedUserFor2FA.first_name} {selectedUserFor2FA.last_name}</p>
                  <p className="text-xs text-gray-500">{selectedUserFor2FA.email}</p>
                </div>
              </div>

              {/* App 2FA */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-primary-200 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                    <Smartphone className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.twoFAApp}</p>
                    <p className="text-xs text-gray-500">{t.twoFAAppDesc}</p>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium mt-1 ${selectedUserFor2FA.is_2fa_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedUserFor2FA.is_2fa_enabled ? <CheckCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {selectedUserFor2FA.is_2fa_enabled ? t.twofaEnabled : t.twofaDisabled}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleToggleApp2FA}
                  disabled={toggling2FA}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedUserFor2FA.is_2fa_enabled
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                  }`}
                >
                  {toggling2FA ? <RefreshCw className="w-3 h-3 animate-spin" /> : (selectedUserFor2FA.is_2fa_enabled ? t.disable : t.enable)}
                </button>
              </div>

              {/* Email 2FA */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-200 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.twoFAEmail}</p>
                    <p className="text-xs text-gray-500">{t.twoFAEmailDesc}</p>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium mt-1 ${selectedUserFor2FA.email_2fa_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedUserFor2FA.email_2fa_enabled ? <CheckCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {selectedUserFor2FA.email_2fa_enabled ? t.twofaEnabled : t.twofaDisabled}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleToggleEmail2FA}
                  disabled={toggling2FA}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedUserFor2FA.email_2fa_enabled
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  }`}
                >
                  {toggling2FA ? <RefreshCw className="w-3 h-3 animate-spin" /> : (selectedUserFor2FA.email_2fa_enabled ? t.disable : t.enable)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAccessDeniedModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAccessDeniedModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"><Ban className="w-4 h-4 text-red-600" /></div>
                <h3 className="text-lg font-semibold text-red-700">{t.accessDenied}</h3>
              </div>
              <button onClick={() => setShowAccessDeniedModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{accessDeniedMessage}</p>
              </div>
              <p className="text-sm text-gray-500 mt-4">{t.accessDeniedDesc}</p>
              <div className="flex justify-end mt-4">
                <button onClick={() => setShowAccessDeniedModal(false)} className="btn-primary">{t.understood}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}